import React from 'react';
import { CardElement, useStripe } from '@stripe/react-stripe-js';
import { Button, Space, Typography, Alert, Divider } from 'antd';
import { useStripeSubscription, CreateSubscriptionParams } from '../../hooks/useStripeSubscription';
import { FONT_FAMILY_BASE } from '../../constants/fonts';

const { Text, Title } = Typography;

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
      fontFamily: FONT_FAMILY_BASE,
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: true,  // 郵便番号を非表示
};

// Stripeプレビューデータの型
export interface StripePreviewData {
  customerId: string;
  subtotal: number;    // 税抜金額
  tax: number;         // 消費税
  total: number;       // 税込合計
  discountAmount: number;  // 割引額
}

export interface CardPaymentFormProps {
  planCode: string;
  planName: string;
  amount: number;  // 税抜金額（フォールバック用）
  prorationAmount?: number;  // 日割り金額（フォールバック用）
  taxRate?: number;  // 税率（デフォルト: 0.1）
  predictionAddonQuantity?: number;
  userAddonQuantity?: number;
  agentCode?: string;
  promotionCode?: string;
  promotionCodeValidated?: boolean;  // 検証済みフラグ
  promotionCodeValid?: boolean;      // 検証結果（有効/無効）
  promotionCodeDiscount?: {          // 割引情報
    percentOff: number | null;
    amountOff: number | null;
    currency: string | null;
    duration: string;
    durationInMonths: number | null;
  } | null;
  customerEmail?: string;  // 請求先メールアドレス
  onSuccess: (subscriptionId: string) => void;
  onCancel?: () => void;
  // StripeAPIから取得した正確なプレビューデータ
  stripePreview?: StripePreviewData | null;
}

export function CardPaymentForm({
  planCode,
  planName,
  amount,
  prorationAmount,
  taxRate = 0.1,
  predictionAddonQuantity,
  userAddonQuantity,
  agentCode,
  promotionCode,
  promotionCodeValidated = false,
  promotionCodeValid = false,
  promotionCodeDiscount,
  customerEmail,
  onSuccess,
  onCancel,
  stripePreview,
}: CardPaymentFormProps) {
  const stripe = useStripe();
  const { createSubscriptionWithCard, loading, error } = useStripeSubscription();

  // Stripeプレビューがある場合はそれを使用、なければクライアント計算
  const useStripeData = !!stripePreview;

  // 割引額の計算（フォールバック用）
  const calculateDiscount = (baseAmount: number): number => {
    if (!promotionCodeDiscount || !promotionCodeValidated || !promotionCodeValid) {
      return 0;
    }

    if (promotionCodeDiscount.percentOff) {
      // パーセント割引
      return Math.round(baseAmount * (promotionCodeDiscount.percentOff / 100));
    } else if (promotionCodeDiscount.amountOff) {
      // 固定金額割引（Stripeはセント単位なので100で割る）
      return Math.round(promotionCodeDiscount.amountOff / 100);
    }

    return 0;
  };

  const discountAmount = calculateDiscount(amount);
  const amountAfterDiscount = amount - discountAmount;
  const taxAmount = Math.round(amountAfterDiscount * taxRate);
  const totalAmount = amountAfterDiscount + taxAmount;

  // 日割り額の計算（Stripeデータがない場合のフォールバック）
  const fallbackProrationAmount = prorationAmount ?? amount;
  const fallbackProrationDiscount = calculateDiscount(fallbackProrationAmount);
  const fallbackProrationAfterDiscount = fallbackProrationAmount - fallbackProrationDiscount;
  const fallbackProrationTax = Math.round(fallbackProrationAfterDiscount * taxRate);
  const fallbackTotal = fallbackProrationAfterDiscount + fallbackProrationTax;

  // 表示用の値（Stripeデータを優先）
  const displayProrationAmount = useStripeData
    ? stripePreview.subtotal + stripePreview.discountAmount  // 割引前の金額
    : fallbackProrationAmount;
  const displayProrationDiscount = useStripeData
    ? stripePreview.discountAmount
    : fallbackProrationDiscount;
  const displayProrationAfterDiscount = useStripeData
    ? stripePreview.subtotal
    : fallbackProrationAfterDiscount;
  const displayProrationTax = useStripeData
    ? stripePreview.tax
    : fallbackProrationTax;
  const displayTotal = useStripeData
    ? stripePreview.total
    : fallbackTotal;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      const params: CreateSubscriptionParams = {
        planCode,
        predictionAddonQuantity,
        userAddonQuantity,
        agentCode,
        promotionCode: promotionCode?.trim() || undefined, // 空文字の場合はundefined
        // プレビューで作成済みの顧客IDを渡す（重複作成を防ぐ）
        existingCustomerId: stripePreview?.customerId,
      };

      const result = await createSubscriptionWithCard(params);
      onSuccess(result.subscriptionId);
    } catch (err) {
      console.error('Subscription creation failed:', err);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 請求先メール表示 */}
        {customerEmail && (
          <Alert
            type="info"
            showIcon
            message="請求先メールアドレス"
            description={
              <Space direction="vertical" size={0}>
                <Text strong>{customerEmail}</Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  このメールアドレスに請求書が送信されます（受信には最大3分程度かかる場合があります）
                </Text>
              </Space>
            }
          />
        )}

        <div>
          <Title level={4}>契約内容</Title>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>プラン</Text>
              <Text strong>{planName}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>月額料金（税抜）</Text>
              <Text>¥{amount.toLocaleString()}</Text>
            </div>
            {discountAmount > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text style={{ color: '#52c41a' }}>
                    ご紹介コード割引
                    {promotionCodeDiscount?.percentOff && ` (${promotionCodeDiscount.percentOff}% OFF)`}
                  </Text>
                  <Text style={{ color: '#52c41a' }}>-¥{discountAmount.toLocaleString()}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>割引後料金（税抜）</Text>
                  <Text strong>¥{amountAfterDiscount.toLocaleString()}</Text>
                </div>
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>消費税（10%）</Text>
              <Text>¥{taxAmount.toLocaleString()}</Text>
            </div>
            <Divider style={{ margin: '8px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text strong>月額合計（税込）</Text>
              <Text strong style={{ fontSize: '18px', color: discountAmount > 0 ? '#52c41a' : undefined }}>
                ¥{totalAmount.toLocaleString()}
              </Text>
            </div>
            {promotionCodeDiscount && promotionCodeDiscount.duration && (
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                {promotionCodeDiscount.duration === 'once' && '※ 割引は初回のみ適用されます'}
                {promotionCodeDiscount.duration === 'forever' && '※ 割引は契約期間中ずっと適用されます'}
                {promotionCodeDiscount.duration === 'repeating' && promotionCodeDiscount.durationInMonths &&
                  `※ 割引は${promotionCodeDiscount.durationInMonths}ヶ月間適用されます`}
              </Text>
            )}
          </Space>
        </div>

        {(prorationAmount && prorationAmount !== amount) || useStripeData ? (
          <Alert
            type="info"
            message="初月は日割り計算"
            description={
              <Space direction="vertical" size="small">
                <Text>今月（残り日数分）の請求額{useStripeData ? '' : '（概算）'}:</Text>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>日割り額（税抜）</Text>
                  <Text>{useStripeData ? '' : '約 '}¥{displayProrationAmount.toLocaleString()}</Text>
                </div>
                {displayProrationDiscount > 0 && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text style={{ color: '#52c41a' }}>ご紹介コード割引</Text>
                      <Text style={{ color: '#52c41a' }}>{useStripeData ? '' : '約 '}-¥{displayProrationDiscount.toLocaleString()}</Text>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>割引後（税抜）</Text>
                      <Text strong>{useStripeData ? '' : '約 '}¥{displayProrationAfterDiscount.toLocaleString()}</Text>
                    </div>
                  </>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>消費税（10%）</Text>
                  <Text>{useStripeData ? '' : '約 '}¥{displayProrationTax.toLocaleString()}</Text>
                </div>
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong>今回のお支払い額{useStripeData ? '' : '（概算）'}</Text>
                  <Text strong style={{ fontSize: '16px', color: displayProrationDiscount > 0 ? '#52c41a' : '#1890ff' }}>
                    {useStripeData ? '' : '約 '}¥{displayTotal.toLocaleString()}
                  </Text>
                </div>
                {!useStripeData && (
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    ※ 実際の請求額は契約時刻により若干異なる場合があります
                  </Text>
                )}
                {useStripeData && (
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    ※ 日割りは秒単位で計算されるため、契約時刻により数円程度異なる場合があります
                  </Text>
                )}
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  ※ 翌月以降は月末に満額（¥{totalAmount.toLocaleString()}）が請求されます
                </Text>
              </Space>
            }
          />
        ) : null}

        {/* ご紹介コード表示 */}
        {promotionCodeValidated && promotionCodeValid && promotionCode ? (
          <Alert
            type="success"
            showIcon
            message="ご紹介コード"
            description={
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text strong code>{promotionCode}</Text>
                {promotionCodeDiscount && (
                  <div>
                    {promotionCodeDiscount.percentOff && (
                      <Text style={{ color: '#52c41a' }}>
                        {promotionCodeDiscount.percentOff}% 割引が適用されます
                      </Text>
                    )}
                    {promotionCodeDiscount.amountOff && (
                      <Text style={{ color: '#52c41a' }}>
                        ¥{Math.round(promotionCodeDiscount.amountOff / 100).toLocaleString()} 割引が適用されます
                      </Text>
                    )}
                  </div>
                )}
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {promotionCodeDiscount?.duration === 'once' && '※ 初回のみ割引適用'}
                  {promotionCodeDiscount?.duration === 'forever' && '※ 契約期間中ずっと割引適用'}
                  {promotionCodeDiscount?.duration === 'repeating' && promotionCodeDiscount.durationInMonths &&
                    `※ ${promotionCodeDiscount.durationInMonths}ヶ月間割引適用`}
                </Text>
              </Space>
            }
          />
        ) : null}

        <div>
          <Title level={5}>クレジットカード情報</Title>
          <div
            style={{
              padding: '12px',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              backgroundColor: '#fff',
            }}
          >
            <CardElement options={CARD_ELEMENT_OPTIONS} />
          </div>
          <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
            カード情報は安全に暗号化されます
          </Text>
        </div>

        {error && (
          <Alert
            type="error"
            message="エラーが発生しました"
            description={error}
            closable
          />
        )}

        <Space>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={!stripe || loading}
            size="large"
          >
            {loading ? '処理中...' : '契約を開始する'}
          </Button>
          {onCancel && (
            <Button onClick={onCancel} disabled={loading} size="large">
              キャンセル
            </Button>
          )}
        </Space>

        <Text type="secondary" style={{ fontSize: '12px' }}>
          「契約を開始する」ボタンをクリックすることで、
          <a href="/legal/terms-privacy" target="_blank">利用規約</a>
          および
          <a href="https://dio-meta.com/privacypolicy/" target="_blank" rel="noopener noreferrer">個人情報保護方針および個人情報の取り扱いについて</a>
          に同意したものとみなされます。
        </Text>
      </Space>
    </form>
  );
}
